import React, { useState, useEffect } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  Popper,
  TextField,
  Button,
  ClickAwayListener,
  Select,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';

import Styled from './DataGrid.styles';

import {
  AnswerValue,
  RenderCellArgs,
  DataGridColumn as Column,
  DataGridCell as Cell,
  DataGridRow as Row,
} from '../../types/DataGridTypes';
import { formatDate } from '../../utils/dateUtils';
import Box from '../Box';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const isActiveCell = ({ rowIndex, column, activeCell }: RenderCellArgs) => (
  activeCell && activeCell.rowNumber === rowIndex && activeCell.field === column.field
);

const renderCell = ({
  rowIndex,
  row,
  column,
  activeCell,
  setActiveCell,
  setEditedAnswer,
}: RenderCellArgs) => {
  if (row[column.field]?.renderCell) {
    return (
      <Styled.TableCell
        key={`${column.field}-${row.id}`}
      >
        {row[column.field].renderCell()}
      </Styled.TableCell>
    );
  }
  if (!column.editable) {
    return (
      <Styled.TableCell
        key={`${column.field}-${row.id}`}
      >
        {column.isSensitive && !!row[column.field]?.render ? '*****' : row[column.field]?.render}
      </Styled.TableCell>
    );
  }
  return (
    <Styled.EditableTableCell
      $isBeingEdited={isActiveCell({ rowIndex, column, activeCell })}
      $hasBeenEdited={row[column.field].hasBeenEdited}
      key={`${column.field}-${row.id.value}`}
      onDoubleClick={(event: React.MouseEvent<HTMLElement>) => {
        setActiveCell({
          rowNumber: rowIndex,
          anchorEl: event.currentTarget,
          ...row[column.field],
          ...column,
        });
        setEditedAnswer(row[column.field].value);
      }}
    >
      {column.isSensitive && !!row[column.field]?.render ? '*****' : row[column.field]?.render}
    </Styled.EditableTableCell>
  );
};

const renderAnswer = (answer: AnswerValue) => {
  if (!answer) {
    return '';
  }
  if (Array.isArray(answer)) {
    return answer.length === 0 ? '' : JSON.stringify(answer);
  }
  if (answer instanceof Date) {
    return formatDate(answer);
  }
  return `${answer}`;
};

const rowsWithRenderedValues = (rows: Row[]): Row[] => (
  rows.map((row): Row => (
    Object.assign(
      {},
      ...Object.entries(row).map(([key, value]) => {
        const obj: { [key: string]: Cell } = {};
        obj[`${key}`] = {
          ...value,
          render: renderAnswer(value.value),
        };
        return obj as Record<string, Cell>;
      }),
    )
  ))
);

const renderFreeFormText = (
  editedAnswer: AnswerValue,
  setEditedAnswer: (answer: AnswerValue) => void,
) => (
  <Box width="320px">
    <TextField
      id="standard-multiline-flexible"
      variant="outlined"
      label=""
      multiline
      maxRows={4}
      minRows={4}
      value={editedAnswer as string}
      fullWidth
      onChange={(event) => setEditedAnswer(event.target.value)}
    />
  </Box>
);

const renderMultipleChoice = (
  activeCell: Cell & Column,
  editedAnswer: AnswerValue,
  setEditedAnswer: (answer: AnswerValue) => void,
) => (
  <Box width="100%">
    <Select
      labelId="label"
      id="select"
      native
      value={editedAnswer as string}
      onChange={(event: React.ChangeEvent<{ value: unknown }>) => setEditedAnswer(event.target.value as string)}
      MenuProps={MenuProps}
      style={{
        minWidth: '100px',
      }}
    >
      <option value="">{}</option>
      {activeCell.valueOptions?.map((option) => (
        <option value={option} key={option}>
          {option}
        </option>
      ))}
    </Select>
  </Box>
);

const renderDateField = (
  editedAnswer: AnswerValue,
  setEditedAnswer: (answer: AnswerValue) => void,
) => {
  const dateValue = editedAnswer && editedAnswer instanceof Date
    && Number.isFinite(editedAnswer.valueOf()) && editedAnswer.toISOString().substring(0, 16);
  return (
    <Box width="320px">
      <TextField
        id="datetime-local"
        label=""
        type="datetime-local"
        defaultValue={dateValue}
        InputLabelProps={{
          shrink: true,
        }}
        onChange={(event) => setEditedAnswer(new Date(event.target.value))}
      />
    </Box>
  );
};

const renderMultiSelect = (
  activeCell: Cell & Column,
  editedAnswer: AnswerValue,
  setEditedAnswer: (answer: AnswerValue) => void,
) => {
  const multiselect = new Set(Array.isArray(editedAnswer) ? editedAnswer : []);
  return (
    <Box width="320px">
      <FormControl component="fieldset">
        <FormGroup>
          {activeCell.valueOptions?.map((option) => (
            <FormControlLabel
              label={option}
              key={option}
              control={(
                <Checkbox
                  checked={multiselect.has(option)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (multiselect.has(event.target.value)) {
                      multiselect.delete(event.target.value);
                    } else {
                      multiselect.add(event.target.value);
                    }
                    setEditedAnswer(Array.from(multiselect));
                  }}
                  value={option}
                />
              )}
            />
          ))}
        </FormGroup>
      </FormControl>
    </Box>
  );
};

const renderEditCellForm = (
  activeCell: Cell & Column,
  editedAnswer: AnswerValue,
  setEditedAnswer: (answer: AnswerValue) => void,
) => {
  switch (activeCell.type) {
  case 'FREE_FORM_TEXT': {
    return renderFreeFormText(editedAnswer, setEditedAnswer);
  }
  case 'MULTIPLE_CHOICE': {
    return renderMultipleChoice(activeCell, editedAnswer, setEditedAnswer);
  }
  case 'DATE_FIELD': {
    return renderDateField(editedAnswer, setEditedAnswer);
  }
  case 'MULTI_SELECT': {
    return renderMultiSelect(activeCell, editedAnswer, setEditedAnswer);
  }
  default:
    // eslint-disable-next-line react/jsx-fragments
    return <React.Fragment>Oops something went wrong...</React.Fragment>;
  }
};

const renderSaveButton = (rows: Row[], onSave: (rows: Row[]) => void) => (
  <Button
    style={{ position: 'absolute', bottom: '50px', right: '50px' }}
    variant="contained"
    color="primary"
    onClick={() => {
      const editedRows = rows.filter((row) => (
        Object.values(row).some((val) => val?.hasBeenEdited)
      ));
      onSave(editedRows);
    }}
  >
    Save Edits
  </Button>
);

type Props = {
  columns: Column[];
  rows: Row[];
  onSave: (rows: Row[]) => void;
  stickyHeader?: boolean;
};

const checkPopperSaveDisabled = (editedAnswer: AnswerValue, activeCell: Cell & Column): boolean => {
  if (!editedAnswer || !activeCell) {
    return true;
  }
  if (editedAnswer instanceof Date) {
    return !(Number.isFinite(editedAnswer.valueOf()) && !!editedAnswer.toISOString());
  }
  return editedAnswer === activeCell.value;
};

const DataGrid: React.FC<Props> = (props: Props) => {
  const { stickyHeader, columns, rows: _rows, onSave } = props;

  const [activeCell, setActiveCell] = useState<Cell & Column>();
  const [editedAnswer, setEditedAnswer] = useState<AnswerValue>();
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    setRows(rowsWithRenderedValues(_rows));
  }, [_rows]);

  const handleClickSaveCell = () => {
    setRows((currentRows) => {
      const currentRowsCopy = [...currentRows];
      currentRowsCopy[activeCell.rowNumber] = {
        ...currentRows[activeCell.rowNumber],
        [activeCell.field]: {
          ...currentRowsCopy[activeCell.rowNumber][activeCell.field],
          value: editedAnswer,
          render: renderAnswer(editedAnswer),
          hasBeenEdited: true,
        },
      };
      return currentRowsCopy;
    });
    setActiveCell(null);
    setEditedAnswer(null);
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <TableContainer
        component={Paper}
        elevation={2}
        style={{ width: '100%', height: 'calc(100% - 150px)', overflow: 'scroll' }}
      >
        <MuiTable aria-label="Table" stickyHeader={stickyHeader}>
          <TableHead>
            <Styled.HeaderRow>
              {columns.map((column, index) => (
                <Styled.HeaderCell key={`${+index}-${column.headerName}`}>
                  {column.headerName}
                </Styled.HeaderCell>
              ))}
            </Styled.HeaderRow>
          </TableHead>
          <TableBody>
            {rows.map((row: Row, rowIndex) => (
              <Styled.TableRow hover key={`${row.id.value}`}>
                {columns.map((column) => (
                  renderCell({
                    rowIndex,
                    row,
                    column,
                    activeCell,
                    setActiveCell,
                    setEditedAnswer,
                  })
                ))}
              </Styled.TableRow>
            ))}
          </TableBody>
        </MuiTable>
        {renderSaveButton(rows, onSave)}
      </TableContainer>
      {!!activeCell && (
        <Popper
          open={!!activeCell}
          anchorEl={activeCell?.anchorEl}
          modifiers={{
            preventOverflow: {
              enabled: true,
              boundariesElement: 'window',
            },
          }}
          style={{
            zIndex: 999999,
            maxHeight: '50vh',
            overflowX: 'scroll',
            boxShadow: '3px 1px 4px 6px #CCC',
          }}
        >
          <ClickAwayListener onClickAway={() => {
            setActiveCell(null);
            setEditedAnswer(null);
          }}
          >
            <Box
              padding="15px"
              width="auto"
              minWidth="200px"
              maxWidth="350px"
              height="100%"
              bgcolor="#FEFEFE"
              borderRadius="3.5px"
              zIndex="99999"
            >
              <Box alignContent="center" display="flex" flexDirection="column">
                <Box width="100%" height="100%">
                  {renderEditCellForm(activeCell, editedAnswer, setEditedAnswer)}
                </Box>
                <Box width="100%" display="flex">
                  <Button
                    style={{ marginTop: '5px', marginLeft: 'auto', marginRight: '0px' }}
                    variant="contained"
                    color="primary"
                    onClick={handleClickSaveCell}
                    disabled={checkPopperSaveDisabled(editedAnswer, activeCell)}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
    </div>
  );
};

export default DataGrid;
